import { useEffect, useMemo, useState } from "react";
import { pricePI } from "@fa/pricing";
import { piRates } from "@fa/rates";
import { piPayloadSchema, todayIso } from "@fa/shared";
import {
  DocumentUploadSection,
  Field,
  FormError,
  IdentitySection,
  PaymentStatus,
  QuoteTable,
  RatesDisclaimer,
  ReviewSummary,
  StepIndicator,
  StepNavigation,
  SubmitButton,
} from "../components/Shared";
import { useFormValidation } from "../hooks/useFormValidation";
import { useStepWizard, type StepDef } from "../hooks/useStepWizard";
import { initiateStk, listMemberDocuments, pollStkStatus, submit, submitDocs, useApiBase, type MemberDocument, type StkStatus } from "../api";

export function PiForm() {
  const api = useApiBase();
  const [identity, setIdentity] = useState({
    icpakMemberNumber: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [firmMode, setFirmMode] = useState(false);
  const [firmName, setFirmName] = useState("");
  const [loi, setLoi] = useState(piRates.limitBands[0]?.limitKes ?? 1000000);
  const [coverStartDate, setCoverStartDate] = useState(todayIso());
  const [reference, setReference] = useState<string | null>(null);
  const [mpesaError, setMpesaError] = useState<string | null>(null);
  const [stkStatus, setStkStatus] = useState<StkStatus | null>(null);
  const [stkReceipt, setStkReceipt] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isStkLoading, setIsStkLoading] = useState(false);
  const [isSubmittingDocs, setIsSubmittingDocs] = useState(false);
  const [docsSubmitted, setDocsSubmitted] = useState(false);
  const [existingDocs, setExistingDocs] = useState<MemberDocument[]>([]);
  const [payLater, setPayLater] = useState(false);

  // Fetch existing documents from SharePoint using phone as the stable folder identifier
  useEffect(() => {
    if (identity.phone) {
      listMemberDocuments(api, identity.phone).then(setExistingDocs).catch(() => {});
    }
  }, [identity.phone, api]);

  const payload = useMemo(
    () => ({
      ...identity,
      applyingAsFirm: firmMode,
      firmName: firmMode ? firmName : undefined,
      limitOfIndemnityKes: loi,
      coverStartDate,
    }),
    [identity, firmMode, firmName, loi, coverStartDate],
  );

  const { errors, allErrors, isValid, parsed, touch, touchAll } = useFormValidation(
    piPayloadSchema,
    payload,
  );
  const quote = isValid ? pricePI({ limitOfIndemnityKes: loi }, piRates) : null;

  const steps: StepDef[] = useMemo(
    () => [
      {
        key: "identity",
        label: "Member Details",
        validate: () => {
          const fields = ["icpakMemberNumber", "firstName", "lastName", "phone", "email"];
          const hasErrors = fields.some((f) => f in allErrors);
          if (hasErrors) { fields.forEach(touch); return false; }
          return true;
        },
      },
      {
        key: "cover",
        label: "Cover Details",
        validate: () => {
          const fields = ["limitOfIndemnityKes", "coverStartDate"];
          if (firmMode) fields.push("firmName");
          const hasErrors = fields.some((f) => f in allErrors);
          if (hasErrors) { fields.forEach(touch); return false; }
          return true;
        },
      },
      {
        key: "review",
        label: "Review & Submit",
      },
    ],
    [allErrors, touch, firmMode],
  );

  const wizard = useStepWizard(steps);

  async function submitDetails() {
    if (!isValid) { touchAll(); return; }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submit(api, "pi", parsed!);
      setReference(res.reference);
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function triggerStk() {
    if (!reference || !quote) return;
    setMpesaError(null);
    setStkReceipt(null);
    setStkStatus("PENDING");
    setIsStkLoading(true);
    try {
      const r = await initiateStk(api, {
        reference,
        phoneNumber: identity.phone,
        amount: String(quote.totalKes),
        accountNumber: identity.icpakMemberNumber,
      });
      if (String(r.ResponseCode) !== "0") {
        setMpesaError(r.Description ?? "Check your phone for the M-Pesa prompt.");
      }
      // STK dispatched — stop showing the button as loading; status spinner takes over
      setIsStkLoading(false);
      const final = await pollStkStatus(api, reference, {
        intervalMs: 4000,
        timeoutMs: 180_000,
        onTick: (s) => {
          setStkStatus(s.status);
          if (s.receipt) setStkReceipt(s.receipt);
          if (s.status !== "PENDING" && s.status !== "UNKNOWN") setMpesaError(null);
        },
      });
      setStkStatus(final.status);
      if (final.receipt) setStkReceipt(final.receipt);
      if (final.status !== "PENDING" && final.status !== "UNKNOWN") setMpesaError(null);
    } catch (e) {
      setMpesaError("Could not send the M-Pesa prompt. Please try again or pay manually via Paybill.");
      setStkStatus(null);
      setIsStkLoading(false);
    }
  }

  async function handleSubmitDocs() {
    setIsSubmittingDocs(true);
    try {
      await submitDocs(api, reference!);
      setDocsSubmitted(true);
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setIsSubmittingDocs(false);
    }
  }

  // After submission: show payment + docs
  if (reference && quote) {
    return (
      <div className="fa-mt-6 fa-space-y-4">
        <div className="fa-rounded-md fa-bg-slate-50 fa-p-4">
          <p>
            Reference <b>{reference}</b>. Pay <b>KES {quote.totalKes.toLocaleString()}</b>{" "}
            via M-Pesa Paybill <b>898200</b>, Account <b>{identity.icpakMemberNumber}</b>.
          </p>
          <div className="fa-flex fa-flex-wrap fa-gap-2 fa-mt-2">
            <SubmitButton onClick={triggerStk} loading={isStkLoading} variant="orange">
              Send me an STK Push
            </SubmitButton>
            {!payLater && stkStatus !== "SUCCESS" && (
              <button
                type="button"
                className="fa-btn-primary fa-text-sm"
                onClick={() => setPayLater(true)}
              >
                Pay Later
              </button>
            )}
          </div>
          <PaymentStatus status={stkStatus} receipt={stkReceipt} error={mpesaError} />
          {payLater && stkStatus !== "SUCCESS" && (
            <p className="fa-text-xs fa-text-amber-700 fa-mt-2">
              You can pay later via M-Pesa Paybill <b>898200</b>, Account <b>{identity.icpakMemberNumber}</b>.
              Please upload your documents below.
            </p>
          )}
        </div>
        <DocumentUploadSection
          reference={reference}
          documents={[
            { kind: "ID", label: "National ID (both sides)" },
            { kind: "KRA_PIN", label: "KRA PIN Certificate" },
            { kind: "PROF_QUAL", label: "Professional Qualification Certificate" },
            { kind: "PRAC_CERT", label: "Current Practising Certificate" },
            ...(firmMode
              ? [{ kind: "CERT_REG", label: "Certificate of Registration (Firm)" }]
              : []),
          ]}
          requiredPayment={!payLater}
          paymentConfirmed={stkStatus === "SUCCESS"}
          onSubmitDocs={handleSubmitDocs}
          isSubmittingDocs={isSubmittingDocs}
          docsSubmitted={docsSubmitted}
          existingDocs={existingDocs}
        />
      </div>
    );
  }

  return (
    <div ref={wizard.containerRef}>
      <StepIndicator
        steps={steps}
        currentIndex={wizard.currentStepIndex}
        completedSteps={wizard.completedSteps}
        onStepClick={wizard.goTo}
      />

      {wizard.currentStep.key === "identity" && (
        <div className="fa-step-enter" key="identity">
          <IdentitySection value={identity} onChange={setIdentity} errors={errors} onBlur={touch} />
        </div>
      )}

      {wizard.currentStep.key === "cover" && (
        <div className="fa-step-enter" key="cover">
          <div className="fa-section fa-section-orange fa-space-y-3">
            <Field id="applyingAsFirm" label="Applying for a Firm's PI Cover?">
              <select
                id="applyingAsFirm"
                value={firmMode ? "YES" : "NO"}
                onChange={(e) => setFirmMode(e.target.value === "YES")}
              >
                <option value="NO">NO</option>
                <option value="YES">YES</option>
              </select>
            </Field>
            {firmMode && (
              <Field id="firmName" label="Firm Name (certificate will be in the firm's name)" error={errors.firmName} required>
                <input id="firmName" value={firmName} onChange={(e) => setFirmName(e.target.value)} onBlur={() => touch("firmName")} />
              </Field>
            )}
            <Field id="limitOfIndemnityKes" label="Limit of Indemnity (for any one claim & any one year)" required>
              <select id="limitOfIndemnityKes" value={loi} onChange={(e) => setLoi(Number(e.target.value))}>
                {piRates.limitBands.map((b) => (
                  <option key={b.limitKes} value={b.limitKes}>
                    KES {b.limitKes.toLocaleString()}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="coverStartDate" label="Cover Start Date" error={errors.coverStartDate} required>
              <input
                id="coverStartDate"
                type="date"
                value={coverStartDate}
                min={todayIso()}
                onChange={(e) => setCoverStartDate(e.target.value)}
                onBlur={() => touch("coverStartDate")}
              />
            </Field>
          </div>
        </div>
      )}

      {wizard.currentStep.key === "review" && (
        <div className="fa-step-enter" key="review">
          <ReviewSummary
            sections={[
              {
                title: "Member Details",
                fields: [
                  { label: "ICPAK Member Number", value: identity.icpakMemberNumber },
                  { label: "First Name", value: identity.firstName },
                  { label: "Last Name", value: identity.lastName },
                  { label: "Phone", value: identity.phone },
                  { label: "Email", value: identity.email },
                ],
              },
              {
                title: "Cover Details",
                fields: [
                  ...(firmMode ? [{ label: "Firm Name", value: firmName }] : []),
                  { label: "Applying as Firm", value: firmMode ? "Yes" : "No" },
                  { label: "Limit of Indemnity", value: `KES ${loi.toLocaleString()}` },
                  { label: "Cover Start Date", value: coverStartDate },
                ],
              },
            ]}
          />
          {quote && (
            <>
              <RatesDisclaimer />
              <QuoteTable title="Professional Indemnity Cover Premium" quote={quote} />
            </>
          )}
          <FormError message={submitError} />
        </div>
      )}

      <StepNavigation
        isFirstStep={wizard.isFirstStep}
        isLastStep={wizard.isLastStep}
        onBack={wizard.goBack}
        onNext={wizard.goNext}
        onSubmit={submitDetails}
        submitLoading={isSubmitting}
        submitDisabled={!isValid}
        submitLabel="Submit Cover Details"
      />
    </div>
  );
}
